import React, { useState, useEffect, useCallback, useRef, startTransition } from 'react';
import axios from '../utils/axiosConfig';
import {
  Typography,
  Paper,
  Box,
  Stack,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Snackbar,
  useTheme
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  CalendarMonth as CalendarIcon,
  CalendarMonth as CalendarMonthIcon,
  People as PeopleIcon,
  ViewList as OneLinerIcon,
  ListAlt as BreakdownIcon,
  Timeline as TimelineIcon
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { API_URL, FEATURE_FLAGS } from '../config';
import * as scriptService from '../services/scriptService';
import { TAG_TYPES } from '../components/SimpleScreenplayViewer';

// Import schedule-related components
import ShootingScheduleTab from '../components/schedule/ShootingScheduleTab';
import DayOutOfDaysTab from '../components/schedule/DayOutOfDaysTab';
import OneLiner from '../components/OneLiner';
import Breakdown from '../components/Breakdown';
import GanttChart from '../components/GanttChart';
import * as ganttTaskService from '../services/ganttTaskService';
import { usePermissions } from '../contexts/PermissionsContext';

function Schedule({ projectId: propProjectId }) {
  // Debug flag to gate verbose logs in this module
  const DEBUG_SCHEDULE = false;
  const dbg = useCallback((...args) => { if (DEBUG_SCHEDULE) console.debug(...args); }, []);
  const theme = useTheme();
  const { projectId: urlProjectId } = useParams();
  // const { currentUser } = useAuth(); // Not currently used
  
  // Use the prop projectId if provided, otherwise use the URL parameter
  const projectId = propProjectId || urlProjectId;
  const { can } = usePermissions();
  const canView = can('schedules.view');
  
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  // Removed unused shootingSchedules and doodSchedules state
  const [ganttTasks, setGanttTasks] = useState([]);
  const [scenes, setScenes] = useState([]);
  const [breaks, setBreaks] = useState([]);
  const [sceneBreakdown, setSceneBreakdown] = useState({});
  const [errorMessages, setErrorMessages] = useState({});
  const [productionPhases, setProductionPhases] = useState([
    { id: 'development', name: 'Development', color: '#3498db' },
    { id: 'pre-production', name: 'Pre-Production', color: '#2ecc71' },
    { id: 'production', name: 'Production', color: '#e74c3c' },
    { id: 'post-production', name: 'Post-Production', color: '#9b59b6' },
    { id: 'distribution', name: 'Distribution', color: '#f39c12' }
  ]);
  
  // Memoized helper: build breakdown map by scene ID using tags and optional content
  const buildSceneBreakdownMap = useCallback((scenesList, tags, content) => {
    const lines = typeof content === 'string' ? content.split('\n') : Array.isArray(content) ? content : [];
    const byId = {};
    // Local helper for diagnostics consistency
    const normalizeSceneNumber = (n) => {
      if (n == null) return '';
      let raw = String(n).toUpperCase().trim();
      raw = raw.replace(/^SC(?:ENE)?\.?\s*/i, '');
      raw = raw.replace(/[^A-Z0-9]/g, '');
      raw = raw.replace(/^0+(?=\d)/, '');
      return raw;
    };
    // Determine scene ranges
    const findSceneRange = (scene, index) => {
      // 1) Prefer explicit numeric ranges on scene when available
      const startCandidates = [scene?.startLine, scene?.lineStart, scene?.range?.start, scene?.firstLine, scene?.start];
      const endCandidates = [scene?.endLine, scene?.lineEnd, scene?.range?.end, scene?.lastLine, scene?.end];
      const sStart = startCandidates.map(v => parseInt(v)).find(v => !isNaN(v));
      const sEnd = endCandidates.map(v => parseInt(v)).find(v => !isNaN(v));
      if (!isNaN(sStart) && !isNaN(sEnd)) {
        return { start: sStart, end: sEnd };
      }
      // 2) Heuristic using script lines: locate heading (INT./EXT.)
      if (Array.isArray(lines) && lines.length > 0) {
        let count = -1;
        let startIdx = -1;
        for (let i = 0; i < lines.length; i++) {
          const l = lines[i];
          if (!l) continue;
          if (/^(INT\.|EXT\.|INT |EXT )/i.test(l.trim())) {
            count++;
            if (count === index) {
              startIdx = i;
              break;
            }
          }
        }
        if (startIdx !== -1) {
          const nextIdx = lines.findIndex((line, idx) => idx > startIdx && line && /^(INT\.|EXT\.|INT |EXT )/i.test(line.trim()));
          return { start: startIdx, end: nextIdx !== -1 ? nextIdx - 1 : lines.length - 1 };
        }
      }
      // 3) Fallback: even partition by scene index
      const total = lines.length || 1000;
      const avg = total / Math.max(1, scenesList.length);
      return { start: Math.floor(index * avg), end: Math.floor((index + 1) * avg) - 1 };
    };

    const perSceneDiag = [];
    let diagRangesExplicit = 0, diagRangesHeuristic = 0, diagRangesPartition = 0;
    // Global diagnostics about tag fields availability
    try {
      const totalTags = (tags || []).length;
      const tagsWithSceneId = (tags || []).filter(t => t.sceneId != null && String(t.sceneId).trim() !== '').length;
      const tagsWithLineNumber = (tags || []).filter(t => !isNaN(parseInt(t.lineNumber))).length;
      dbg('[Schedule] tag field availability', { totalTags, tagsWithSceneId, tagsWithLineNumber });
    } catch {}
    // Compute script-order index per scene (by numeric scene number or displayIndex as fallback)
    const toNormParts = (s) => {
      const norm = (s || '').toString().toUpperCase();
      const m = norm.match(/^([0-9]+)([A-Z]*)$/);
      if (!m) return { n: Number.POSITIVE_INFINITY, suf: 'ZZZ' };
      return { n: parseInt(m[1]), suf: m[2] || '' };
    };
    const scriptOrdered = (scenesList || [])
      .map((s, i) => {
        const id = String(s.id || s._id || `scene_${i}`);
        const norm = normalizeSceneNumber(s.sceneNumber?.standard || s.sceneNumber || s.number || s.displayIndex || '');
        const parts = toNormParts(norm);
        const disp = parseInt(s.displayIndex);
        return { s, id, norm, parts, disp };
      })
      .sort((a, b) => {
        // Primary: natural scene number ordering (1 < 1A < 2 < 2A ...)
        if (a.parts.n !== b.parts.n) return a.parts.n - b.parts.n;
        if (a.parts.suf !== b.parts.suf) return a.parts.suf.localeCompare(b.parts.suf);
        // Secondary: displayIndex if available
        const aHasDisp = !isNaN(a.disp);
        const bHasDisp = !isNaN(b.disp);
        if (aHasDisp && bHasDisp && a.disp !== b.disp) return a.disp - b.disp;
        return 0;
      });
    const scriptIdxById = new Map(scriptOrdered.map((x, i) => [x.id, i]));

    (scenesList || []).forEach((scene, idx) => {
      const sidForIdx = String(scene.id || scene._id || `scene_${idx}`);
      const scriptIdx = scriptIdxById.has(sidForIdx) ? scriptIdxById.get(sidForIdx) : idx;
      const { start, end } = findSceneRange(scene, scriptIdx);
      if (!isNaN(parseInt(scene?.startLine)) || !isNaN(parseInt(scene?.lineStart)) || !isNaN(parseInt(scene?.range?.start))) {
        diagRangesExplicit++;
      } else if (Array.isArray(lines) && lines.length > 0) {
        diagRangesHeuristic++;
      } else {
        diagRangesPartition++;
      }
      // Always use string keys. Keep identical to scheduleFormatter sid logic so lookups align
      const sceneId = String(scene.id || scene._id || `scene_${idx}`);
      // Prepare scene metadata to attach to the breakdown entry
      // CRITICAL: Do NOT use (idx + 1) as fallback - this causes breakdown items to shift when scenes are reordered
      const sceneNumberRaw = scene.sceneNumber?.standard || scene.sceneNumber?.original || scene.sceneNumber || scene.number;
      
      // Debug: Log breakdown map building for first 5 scenes
      if (idx < 5) {
        console.log(`[Breakdown Map] Scene ${idx}:`, {
          sceneId,
          'scene.number': scene.number,
          'scene.sceneNumber': scene.sceneNumber,
          sceneNumberRaw,
          normalized: normalizeSceneNumber(sceneNumberRaw)
        });
      }
      
      const heading = scene.heading || scene.text || '';
      const locationType = scene.location_type || '';
      const timeOfDay = scene.time || '';
      const locationName = scene.location || '';
      const sceneInfo = {
        id: sceneId,
        number: sceneNumberRaw,
        numberNormalized: normalizeSceneNumber(sceneNumberRaw),
        heading,
        location_type: locationType, // e.g., INT, EXT, INT/EXT
        time: timeOfDay, // e.g., DAY, NIGHT
        location: locationName,
        range: { start, end }
      };
      let matchedById = 0, matchedByNumber = 0, matchedByLine = 0;
      const sceneTags = tags.filter(t => {
        // Prefer exact sceneId match if provided
        if (t.sceneId != null && String(t.sceneId) === sceneId) {
          matchedById++;
          if (idx < 3) {
            console.log(`[Tag Match BY ID] "${t.text}" → Scene ${sceneNumberRaw}`, { tagSceneId: t.sceneId, sceneId });
          }
          return true;
        }
        // Next, match by scene number if tag provides one
        // CRITICAL: Match by scene number BEFORE falling back to line ranges
        // This ensures that when scenes are reordered in OneLiner, breakdown items
        // stay associated with their original scene numbers
        const tagNumCandidates = [
          t.sceneNumber?.standard,
          t.sceneNumber?.original,
          t.sceneNumber,
          t.number,
          t.sceneNo,
          t.scene,
          t.scene_number,
          t.tscene,
          t.targetScene,
        ].map(normalizeSceneNumber).filter(Boolean);
        if (tagNumCandidates.length) {
          const hasNumMatch = tagNumCandidates.includes(sceneInfo.numberNormalized);
          if (hasNumMatch) {
            matchedByNumber++;
            if (idx < 3) {
              console.log(`[Tag Match BY NUMBER] "${t.text}" → Scene ${sceneNumberRaw}`, { tagNumber: tagNumCandidates[0], sceneNumber: sceneInfo.numberNormalized });
            }
            return true;
          }
        }
        // DISABLED: Line range fallback is unreliable after scene reordering
        // Tags MUST have sceneId or sceneNumber to be matched
        // This prevents breakdown items from shifting to wrong scenes based on position
        if (idx < 3) {
          const ln = parseInt(t.lineNumber);
          if (!isNaN(ln) && ln >= start && ln <= end) {
            console.log(`[Tag SKIPPED - No Scene ID/Number] "${t.text}" at line ${ln}`, { 
              tagLine: ln, 
              sceneRange: `${start}-${end}`,
              tagSceneId: t.sceneId,
              tagNumber: t.number,
              message: 'Tag needs sceneId or sceneNumber to be matched'
            });
          }
        }
        return false; // Don't match by line range
      });

      const grouped = sceneTags.reduce((acc, tag) => {
        let category = tag.type || 'PROPS';
        if (category === 'CAST') category = 'CHARACTER';
        if (!TAG_TYPES[category]) category = 'PROPS';
        if (!acc[category]) acc[category] = [];
        // Attach light scene linkage so each tag carries scene context
        const tagWithScene = {
          ...tag,
          sceneId: String(tag.sceneId || sceneId),
          sceneNumber: tag.sceneNumber || sceneNumberRaw,
          sceneNumberNormalized: normalizeSceneNumber(tag.sceneNumber || sceneNumberRaw)
        };
        acc[category].push(tagWithScene);
        return acc;
      }, {});

      // Sort each category: AUTO first (items with lineNumber) by lineNumber asc; then MANUAL (no lineNumber) preserving insertion order
      Object.keys(grouped).forEach((catKey) => {
        const arr = Array.isArray(grouped[catKey]) ? grouped[catKey] : [];
        const withLine = [];
        const withoutLine = [];
        for (const t of arr) {
          const ln = parseInt(t?.lineNumber);
          if (!isNaN(ln)) withLine.push(t); else withoutLine.push(t);
        }
        withLine.sort((a, b) => parseInt(a.lineNumber) - parseInt(b.lineNumber));
        grouped[catKey] = [...withLine, ...withoutLine];
      });

      // Compute quick counts and camera notes
      const counts = Object.keys(grouped).reduce((m, k) => {
        m[k] = (grouped[k] || []).length;
        return m;
      }, {});
      const cameraNotes = [
        ...(grouped.CAMERA || []),
        ...(grouped.PRODUCTION_NOTES || []).filter(t => /camera|lens|frame|shot|coverage/i.test(t.text || ''))
      ].map(t => t.text);

      byId[sceneId] = { grouped, counts, cameraNotes, sceneInfo };
      // Targeted debug: show matching for any tag mentioning 'insect'
      try {
        const insect = (sceneTags || []).filter(x => /insect/i.test(x.text || ''));
        if (insect.length) {
          console.debug('[Schedule][insect] scene', sceneInfo.number, {
            sceneId,
            matchedById,
            matchedByNumber,
            matchedByLine,
            samples: insect.map(x => ({
              text: x.text,
              sceneId: x.sceneId,
              sceneNumber: x.sceneNumber,
              number: x.number,
              tscene: x.tscene,
              targetScene: x.targetScene,
              lineNumber: x.lineNumber
            }))
          });
        }
      } catch {}
      // Collect diagnostics for first 25 scenes
      if (idx < 25) {
        perSceneDiag.push({
          idx,
          sceneId,
          num: sceneInfo.number,
          numNorm: sceneInfo.numberNormalized,
          matchedById,
          matchedByNumber,
          matchedByLine,
          groupedKeys: Object.keys(grouped || {})
        });
      }
    });
    try {
      if (perSceneDiag.length) {
        dbg('[Schedule] per-scene tag matching (id or line, first 25):', perSceneDiag);
        dbg('[Schedule] range sources', { explicit: diagRangesExplicit, heuristic: diagRangesHeuristic, partition: diagRangesPartition });
      }
    } catch {}
    return byId;
  }, [dbg]);

  // Backfill existing tags that are missing sceneId/sceneNumber by deriving the owning scene via line ranges.
  // Returns { updatedTags, changed } and persists changes if any.
  const backfillTagSceneIdentifiers = useCallback(async (scenesList, tags, content, activeScriptId) => {
    try {
      const lines = typeof content === 'string' ? content.split('\n') : Array.isArray(content) ? content : [];
      const normalizeSceneNumber = (n) => {
        if (n == null) return '';
        let raw = String(n).toUpperCase().trim();
        raw = raw.replace(/^SC(?:ENE)?\.?\s*/i, '');
        raw = raw.replace(/[^A-Z0-9]/g, '');
        raw = raw.replace(/^0+(?=\d)/, '');
        return raw;
      };
      // Reuse the same range logic as buildSceneBreakdownMap to ensure consistent mapping
      const findSceneRange = (scene, index) => {
        const startCandidates = [scene?.startLine, scene?.lineStart, scene?.range?.start, scene?.firstLine, scene?.start];
        const endCandidates = [scene?.endLine, scene?.lineEnd, scene?.range?.end, scene?.lastLine, scene?.end];
        const sStart = startCandidates.map(v => parseInt(v)).find(v => !isNaN(v));
        const sEnd = endCandidates.map(v => parseInt(v)).find(v => !isNaN(v));
        if (!isNaN(sStart) && !isNaN(sEnd)) return { start: sStart, end: sEnd };
        if (Array.isArray(lines) && lines.length > 0) {
          let count = -1;
          let startIdx = -1;
          for (let i = 0; i < lines.length; i++) {
            const l = lines[i];
            if (!l) continue;
            if (/^(INT\.|EXT\.|INT |EXT )/i.test(l.trim())) {
              count++;
              if (count === index) { startIdx = i; break; }
            }
          }
          if (startIdx !== -1) {
            const nextIdx = lines.findIndex((line, idx) => idx > startIdx && line && /^(INT\.|EXT\.|INT |EXT )/i.test(line.trim()));
            return { start: startIdx, end: nextIdx !== -1 ? nextIdx - 1 : lines.length - 1 };
          }
        }
        const total = lines.length || 1000;
        const avg = total / Math.max(1, scenesList.length);
        return { start: Math.floor(index * avg), end: Math.floor((index + 1) * avg) - 1 };
      };

      // Precompute scene ranges and identity using script order (not OneLiner order)
      const toNormParts = (s) => {
        const norm = (s || '').toString().toUpperCase();
        const m = norm.match(/^([0-9]+)([A-Z]*)$/);
        if (!m) return { n: Number.POSITIVE_INFINITY, suf: 'ZZZ' };
        return { n: parseInt(m[1]), suf: m[2] || '' };
      };
      const scriptOrdered = (scenesList || [])
        .map((s, i) => {
          const id = String(s.id || s._id || `scene_${i}`);
          const norm = normalizeSceneNumber(s.sceneNumber?.standard || s.sceneNumber || s.number || s.displayIndex || '');
          const parts = toNormParts(norm);
          const disp = parseInt(s.displayIndex);
          return { s, id, norm, parts, disp };
        })
        .sort((a, b) => {
          if (a.parts.n !== b.parts.n) return a.parts.n - b.parts.n;
          if (a.parts.suf !== b.parts.suf) return a.parts.suf.localeCompare(b.parts.suf);
          const aHasDisp = !isNaN(a.disp);
          const bHasDisp = !isNaN(b.disp);
          if (aHasDisp && bHasDisp && a.disp !== b.disp) return a.disp - b.disp;
          return 0;
        });
      const sceneMeta = scriptOrdered.map((entry, idx) => {
        const scene = entry.s;
        const range = findSceneRange(scene, idx);
        const sceneId = String(scene.id || scene._id || `scene_${idx}`);
        const sceneNumberRaw = scene.sceneNumber?.standard || scene.sceneNumber?.original || scene.sceneNumber || scene.number;
        return { idx, range, sceneId, sceneNumberRaw, sceneNumberNorm: normalizeSceneNumber(sceneNumberRaw) };
      });

      let changed = 0;
      const updatedTags = (tags || []).map((t) => {
        const hasId = t.sceneId != null && String(t.sceneId).trim() !== '';
        const numField = t.sceneNumber ?? t.number ?? t.scene ?? t.scene_number;
        const normNum = normalizeSceneNumber(numField);
        // Path A: If tag carries a scene number, ensure sceneId matches the scene with that number
        if (normNum) {
          const ownerByNum = sceneMeta.find(m => m.sceneNumberNorm === normNum);
          if (ownerByNum) {
            // If missing or mismatched id/number, correct it
            const currentIdStr = hasId ? String(t.sceneId) : '';
            if (!hasId || currentIdStr !== ownerByNum.sceneId) {
              changed++;
              return { ...t, sceneId: ownerByNum.sceneId, sceneNumber: ownerByNum.sceneNumberRaw };
            }
            // Id already matches; ensure sceneNumber is populated
            if (!t.sceneNumber) {
              changed++;
              return { ...t, sceneNumber: ownerByNum.sceneNumberRaw };
            }
            return t;
          }
        }
        // Path B: fallback by lineNumber range if no usable scene number on tag
        const ln = parseInt(t.lineNumber);
        if (isNaN(ln)) return t;
        const owner = sceneMeta.find(m => ln >= m.range.start && ln <= m.range.end);
        if (!owner) return t;
        changed++;
        return { ...t, sceneId: owner.sceneId, sceneNumber: owner.sceneNumberRaw };
      });

      if (changed > 0 && activeScriptId) {
        try {
          await scriptService.updateScriptTags(activeScriptId, projectId, updatedTags);
          window.dispatchEvent(new CustomEvent('scriptTagsUpdated', { detail: { projectId } }));
        } catch (e) {
          console.warn('Backfill: failed to persist tag updates', e);
        }
      }
      return { updatedTags, changed };
    } catch (e) {
      console.warn('Backfill: failed', e);
      return { updatedTags: Array.isArray(tags) ? tags : [], changed: 0 };
    }
  }, [projectId]);
  
  const dayForDaysEnabled = FEATURE_FLAGS.scheduleDayForDays;
  const dayOutTabIndex = dayForDaysEnabled ? 3 : null;
  const timelineTabIndex = dayForDaysEnabled ? 4 : 3;

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  
  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };
  
  // Fetch shooting schedules, DOOD schedules, and gantt tasks when the component loads
  useEffect(() => {
    const fetchSchedules = async () => {
      if (!projectId) return;
      if (!canView) return;
      
      setLoading(true);
      setError(null);
      setSnackbar({ open: false, message: '', severity: 'success' });

      try {
        // Keep a local copy of the most recent scenes list for building breakdowns
        let currentScenes = [];
        
        // Parallelize independent API calls for better performance
        const [ganttTasksData, scenesResponse, breaksResponse] = await Promise.allSettled([
          ganttTaskService.getProjectGanttTasks(projectId),
          axios.get(`/api/v1/scripts/project/${projectId}/scenes`, {
            headers: {
              'x-auth-token': localStorage.getItem('token') || '',
              'Authorization': localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : undefined
            }
          }),
          fetch(`${API_URL}/api/v1/scripts/${projectId}/breaks`, {
            headers: {
              'x-auth-token': localStorage.getItem('token') || '',
              'Content-Type': 'application/json'
            }
          })
        ]);

        // Handle gantt tasks
        if (ganttTasksData.status === 'fulfilled') {
          setGanttTasks(ganttTasksData.value);
        } else {
          console.warn('Failed to fetch gantt tasks:', ganttTasksData.reason);
          setGanttTasks([]);
        }

        // Handle scenes (used by ShootingSchedule)
        if (scenesResponse.status === 'fulfilled' && scenesResponse.value.status === 200) {
          try {
            const response = scenesResponse.value;
            const rawScenes = Array.isArray(response.data?.scenes)
              ? response.data.scenes
              : Array.isArray(response.data)
                ? response.data
                : [];
            // Minimal normalization: ensure .number and .pages fields exist
            // CRITICAL: Do NOT use idx+1 as fallback for scene number - this causes breakdown mismatch after reordering
            let normalized = rawScenes.map((s, idx) => {
              const sceneNumber = s.sceneNumber?.standard || s.sceneNumber || s.number || s.displayIndex;
              // Debug: Log first 5 scenes to verify scene numbers are preserved
              if (idx < 5) {
                console.log(`[Schedule] Scene ${idx}:`, {
                  id: s.id || s._id,
                  'sceneNumber.standard': s.sceneNumber?.standard,
                  'sceneNumber.original': s.sceneNumber?.original,
                  sceneNumberFull: s.sceneNumber,
                  number: s.number,
                  displayIndex: s.displayIndex,
                  resolvedNumber: sceneNumber,
                  order: s.order
                });
              }
              return {
                id: String(s.id || s._id || `scene_${idx}`),
                number: sceneNumber,
                pages: parseFloat(s.pages) || 0,
                heading: s.heading || s.text || '',
                location_type: s.location_type || '',
                time: s.time || '',
                area: s.area || '',
                location: s.location || '',
                // Include characters from API (OneLiner-compatible) if available
                characters: Array.isArray(s.characters)
                  ? s.characters.map(c => typeof c === 'string' ? c : (c.name || c.characterName || '')).filter(Boolean)
                  : Array.isArray(s.cast)
                    ? s.cast.map(c => typeof c === 'string' ? c : (c.name || c.characterName || '')).filter(Boolean)
                    : [],
                // Ensure a stable order for scheduleFormatter to align with afterSceneIndex
                order: (typeof s.order === 'number') ? s.order : (typeof s.index === 'number') ? s.index : idx
              };
            });

            // Sort scenes strictly by OneLiner order (do not re-order by number to keep dayBreak indices aligned)
            try {
              normalized = normalized
                .map((s, i) => ({ ...s, __idx: i }))
                .sort((a, b) => {
                  const ao = typeof a.order === 'number' ? a.order : Number.MAX_SAFE_INTEGER;
                  const bo = typeof b.order === 'number' ? b.order : Number.MAX_SAFE_INTEGER;
                  if (ao !== bo) return ao - bo;
                  return a.__idx - b.__idx;
                })
                .map(({ __idx, ...rest }) => rest);
            } catch (e) {
              console.warn('Schedule: scene sort skipped due to error', e);
            }

            // Fallback: derive scenes from script content if API returns empty
            if (normalized.length === 0) {
              try {
                const scripts = await scriptService.getProjectScripts(projectId);
                if (Array.isArray(scripts) && scripts.length > 0) {
                  const activeScript = scripts[0];
                  const scriptData = await scriptService.getScriptContent(activeScript._id, 'latest', projectId);
                  const content = scriptData?.content || '';
                  if (content) {
                    const lines = content.split('\n');
                    const headingRegex = /^(INT\.|EXT\.|INT |EXT )/i;
                    const indices = [];
                    lines.forEach((line, idx) => {
                      if (line && headingRegex.test(line.trim())) indices.push(idx);
                    });
                    if (indices.length) {
                      normalized = indices.map((start, i) => ({
                        id: `scene_${i}`,
                        number: i + 1,
                        pages: 0,
                        heading: lines[start] || `Scene ${i + 1}`,
                        location_type: '',
                        time: '',
                        area: '',
                        location: '',
                        order: i,
                        // Provide rough line ranges for downstream breakdown mapping
                        startLine: start,
                        endLine: (i < indices.length - 1 ? indices[i + 1] - 1 : lines.length - 1)
                      }));
                      dbg('Schedule: derived scenes from script content', { count: normalized.length });
                    }
                  }
                }
              } catch (fallbackErr) {
                console.warn('Schedule: fallback scene derivation failed', fallbackErr);
              }
            }

            currentScenes = normalized;
            startTransition(() => {
              setScenes(normalized);
            });
            dbg('Schedule: scenes loaded', { count: normalized.length });
          } catch (e) {
            console.warn('Scenes fetch error:', e);
            setScenes([]);
          }
        } else {
          console.warn('Failed to fetch scenes for schedule:', scenesResponse.reason || scenesResponse.status);
          setScenes([]);
        }

        // Handle breaks (day breaks, lunch, company moves) using parallelized response
        if (breaksResponse.status === 'fulfilled' && breaksResponse.value.ok) {
          try {
            const bdata = await breaksResponse.value.json();
            const rawBreaks = Array.isArray(bdata) ? bdata : [];
            // Normalize for the shared formatter: ensure type and afterSceneIndex
            const maxSceneIndex = Math.max(0, (currentScenes?.length || 0) - 1);
            const normalizedBreaks = rawBreaks
              .filter(b => typeof b.afterSceneIndex === 'number' && b.type)
              .map(b => ({
                _id: b._id,
                type: b.type, // Expected to be DAY_BREAK | LUNCH | COMPANY_MOVE
                // Keep provided index; do not clamp day breaks so we can filter orphaned ones below
                afterSceneIndex: b.afterSceneIndex
              }))
              // Filter out orphaned DAY_BREAKs that target beyond the last scene (mirrors OneLiner)
              .filter(b => b.type !== 'DAY_BREAK' || b.afterSceneIndex <= maxSceneIndex)
              .sort((a, b) => a.afterSceneIndex - b.afterSceneIndex);
            if (rawBreaks.length) {
              const dayBreaksCount = rawBreaks.filter(b => b.type === 'DAY_BREAK').length;
              const validDayBreaksCount = normalizedBreaks.filter(b => b.type === 'DAY_BREAK').length;
              if (dayBreaksCount !== validDayBreaksCount) {
                dbg(`Schedule: Filtered out ${dayBreaksCount - validDayBreaksCount} orphaned day breaks (maxSceneIndex=${maxSceneIndex})`);
              }
            }
            startTransition(() => {
              setBreaks(normalizedBreaks);
            });
          } catch (e) {
            console.warn('Breaks fetch error:', e);
            setBreaks([]);
          }
        } else {
          console.warn('Failed to fetch breaks for schedule:', breaksResponse.reason || breaksResponse.status);
          setBreaks([]);
        }

        // Fetch script list and tags to compute breakdown per scene
        try {
          const scripts = await scriptService.getProjectScripts(projectId);
          if (Array.isArray(scripts) && scripts.length > 0) {
            const activeScript = scripts[0];
            const scriptData = await scriptService.getScriptContent(activeScript._id, 'latest', projectId);
            const content = scriptData?.content || '';
            const tags = Array.isArray(scriptData?.tags) ? scriptData.tags : [];
            // Backfill missing scene identifiers on tags, then build
            const { updatedTags, changed } = await backfillTagSceneIdentifiers(currentScenes, tags, content, activeScript._id);
            const sceneMap = buildSceneBreakdownMap(currentScenes, updatedTags, content);
            dbg('Schedule: sceneBreakdown built', {
              scenesCount: currentScenes?.length || 0,
              tagsCount: (updatedTags?.length) || 0,
              backfilled: changed,
              breakdownKeys: Object.keys(sceneMap)
            });
            startTransition(() => {
              setSceneBreakdown(sceneMap);
            });
          } else {
            setSceneBreakdown({});
          }
        } catch (e) {
          console.warn('Script tags fetch error:', e);
          setSceneBreakdown({});
        }
        // End loading immediately (no artificial delay)
        setLoading(false);
      } catch (err) {
        console.error('Error fetching schedules:', err);
        setError('Failed to load schedules. Please try again later.');
        setLoading(false);
      }
    };
    
    fetchSchedules();
  }, [projectId, canView, buildSceneBreakdownMap, dbg]);

  // When switching to Shooting Schedule tab, force refresh of breakdown from latest script tags/content
  useEffect(() => {
    const refreshIfNeeded = async () => {
      if (activeTab !== 2 || !projectId) return;
      try {
        const scripts = await scriptService.getProjectScripts(projectId);
        if (!Array.isArray(scripts) || scripts.length === 0) {
          setSceneBreakdown({});
          return;
        }
        const activeScript = scripts[0];
        const scriptData = await scriptService.getScriptContent(activeScript._id, 'latest', projectId);
        const content = scriptData?.content || '';
        const tags = Array.isArray(scriptData?.tags) ? scriptData.tags : [];
        const { updatedTags } = await backfillTagSceneIdentifiers(scenes, tags, content, activeScript._id);
        const sceneMap = buildSceneBreakdownMap(scenes, updatedTags, content);
        setSceneBreakdown(sceneMap);
        dbg('Schedule: rebuilt breakdown on tab activation', {
          scenesCount: scenes?.length || 0,
          tagsCount: (updatedTags?.length) || 0
        });
      } catch (e) {
        console.warn('Schedule: failed breakdown refresh on tab activation', e);
      }
    };
    refreshIfNeeded();
  }, [activeTab, projectId, scenes, buildSceneBreakdownMap, dbg]);


  // Debounced tag update handler to prevent excessive rebuilds
  const debouncedTagUpdateRef = useRef(null);
  
  // Listen for screenplay tag updates and rebuild breakdown automatically
  useEffect(() => {
    const handleScriptTagsUpdated = async (evt) => {
      try {
        const { projectId: evtProjectId } = evt?.detail || {};
        if (!evtProjectId || evtProjectId !== projectId) return;

        // Clear existing debounce timer
        if (debouncedTagUpdateRef.current) {
          clearTimeout(debouncedTagUpdateRef.current);
        }

        // Debounce rapid tag updates (300ms delay)
        debouncedTagUpdateRef.current = setTimeout(async () => {
          try {
            // Fetch latest script content and tags
            const scripts = await scriptService.getProjectScripts(projectId);
            if (!Array.isArray(scripts) || scripts.length === 0) {
              startTransition(() => {
                setSceneBreakdown({});
              });
              return;
            }
            const activeScript = scripts[0];
            const scriptData = await scriptService.getScriptContent(activeScript._id, 'latest', projectId);
            const content = scriptData?.content || '';
            const tags = Array.isArray(scriptData?.tags) ? scriptData.tags : [];

            // Rebuild using current scenes in state (non-urgent update)
            const sceneMap = buildSceneBreakdownMap(scenes, tags, content);
            startTransition(() => {
              setSceneBreakdown(sceneMap);
            });
            dbg('Schedule: sceneBreakdown refreshed after tag update', {
              scenesCount: scenes?.length || 0,
              tagsCount: tags?.length || 0
            });
          } catch (e) {
            console.warn('Schedule: failed to refresh breakdown after tag update', e);
          }
        }, 300);
      } catch (e) {
        console.warn('Schedule: failed to setup debounced tag update', e);
      }
    };

    window.addEventListener('scriptTagsUpdated', handleScriptTagsUpdated);
    return () => {
      window.removeEventListener('scriptTagsUpdated', handleScriptTagsUpdated);
      if (debouncedTagUpdateRef.current) {
        clearTimeout(debouncedTagUpdateRef.current);
      }
    };
  }, [projectId, scenes, buildSceneBreakdownMap, dbg]);

  // Listen for scene reordering events from OneLiner and refresh breakdown
  useEffect(() => {
    const handleScenesReordered = async (evt) => {
      try {
        const { projectId: evtProjectId } = evt?.detail || {};
        if (!evtProjectId || evtProjectId !== projectId) return;

        console.log('[Schedule] Scenes reordered, refreshing breakdown map...');
        
        // Fetch latest scenes with new order
        const scenesResponse = await axios.get(`/api/v1/scripts/project/${projectId}/scenes`, {
          headers: {
            'x-auth-token': localStorage.getItem('token') || '',
            'Authorization': localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : undefined
          }
        });

        if (scenesResponse.status === 200) {
          const rawScenes = Array.isArray(scenesResponse.data?.scenes)
            ? scenesResponse.data.scenes
            : Array.isArray(scenesResponse.data)
              ? scenesResponse.data
              : [];
          
          // Normalize and sort by order
          // CRITICAL: Do NOT use idx+1 as fallback for scene number
          let normalized = rawScenes.map((s, idx) => ({
            id: String(s.id || s._id || `scene_${idx}`),
            number: s.sceneNumber?.standard || s.sceneNumber || s.number || s.displayIndex,
            pages: parseFloat(s.pages) || 0,
            heading: s.heading || s.text || '',
            location_type: s.location_type || '',
            time: s.time || '',
            area: s.area || '',
            location: s.location || '',
            characters: Array.isArray(s.characters)
              ? s.characters.map(c => typeof c === 'string' ? c : (c.name || c.characterName || '')).filter(Boolean)
              : [],
            order: (typeof s.order === 'number') ? s.order : idx
          }));

          normalized = normalized.sort((a, b) => {
            const ao = typeof a.order === 'number' ? a.order : Number.MAX_SAFE_INTEGER;
            const bo = typeof b.order === 'number' ? b.order : Number.MAX_SAFE_INTEGER;
            if (ao !== bo) return ao - bo;
            return 0;
          });

          startTransition(() => {
            setScenes(normalized);
          });

          // Rebuild breakdown map with new scene order
          const scripts = await scriptService.getProjectScripts(projectId);
          if (Array.isArray(scripts) && scripts.length > 0) {
            const activeScript = scripts[0];
            const scriptData = await scriptService.getScriptContent(activeScript._id, 'latest', projectId);
            const content = scriptData?.content || '';
            const tags = Array.isArray(scriptData?.tags) ? scriptData.tags : [];
            const { updatedTags, changed } = await backfillTagSceneIdentifiers(normalized, tags, content, activeScript._id);
            const sceneMap = buildSceneBreakdownMap(normalized, updatedTags, content);
            
            startTransition(() => {
              setSceneBreakdown(sceneMap);
            });
            
            console.log('[Schedule] Breakdown map refreshed after scene reordering', {
              scenesCount: normalized.length,
              tagsCount: (updatedTags?.length) || 0,
              backfilled: changed,
              breakdownKeys: Object.keys(sceneMap).length
            });
          }
        }
      } catch (e) {
        console.warn('[Schedule] Failed to refresh breakdown after scene reordering', e);
      }
    };

    window.addEventListener('scenesReordered', handleScenesReordered);
    return () => {
      window.removeEventListener('scenesReordered', handleScenesReordered);
    };
  }, [projectId, buildSceneBreakdownMap]);
  
  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Permission warning */}
      {!canView && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          You do not have permission to view schedules (requires schedules.view).
        </Alert>
      )}

      {/* Error message */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {/* Loading indicator */}
      {loading && (
        <Box sx={{ width: '100%', mb: 2, display: 'flex', alignItems: 'center' }}>
          <CircularProgress size={20} sx={{ mr: 1 }} />
          <Typography variant="body2" color="text.secondary">
            Loading schedules...
          </Typography>
        </Box>
      )}

      {/* Header with Tabs */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        borderBottom: (theme) => `1px solid ${alpha(theme.palette.divider, 0.12)}`,
        py: 0.5,
        px: 1
      }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1.5,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`
            }}
          >
            <CalendarMonthIcon sx={{ color: 'white', fontSize: 18 }} />
          </Box>
          <Typography variant="subtitle1" sx={{
            fontWeight: 600,
            color: (theme) => theme.palette.text.primary
          }}>
            Schedule
          </Typography>
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange}
            aria-label="schedule tabs"
            sx={{
              minHeight: 36,
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.8rem',
                minHeight: 36,
                minWidth: 70,
                py: 0.25,
                px: 1
              },
              '& .MuiTabs-indicator': {
                height: 2,
                borderRadius: 1
              }
            }}
          >
            <Tab 
              icon={<OneLinerIcon />} 
              iconPosition="start" 
              label="One Liner" 
              id="tab-0" 
              aria-controls="tabpanel-0" 
            />
            <Tab 
              icon={<BreakdownIcon />} 
              iconPosition="start" 
              label="Breakdown" 
              id="tab-1" 
              aria-controls="tabpanel-1" 
            />
            <Tab 
              icon={<CalendarIcon />} 
              iconPosition="start" 
              label="Shooting Schedule" 
              id="tab-2" 
              aria-controls="tabpanel-2" 
            />
            {dayForDaysEnabled && (
              <Tab 
                icon={<PeopleIcon />} 
                iconPosition="start" 
                label="Day Out of Days" 
                id={`tab-${dayOutTabIndex}`} 
                aria-controls={`tabpanel-${dayOutTabIndex}`} 
              />
            )}
            <Tab 
              icon={<TimelineIcon />} 
              iconPosition="start" 
              label="Timeline" 
              id={`tab-${timelineTabIndex}`} 
              aria-controls={`tabpanel-${timelineTabIndex}`} 
            />
          </Tabs>
        </Stack>
      </Box>
        
        {/* Tab Panels */}
        {/* One Liner Tab Panel */}
        <Box 
          role="tabpanel" 
          hidden={activeTab !== 0} 
          id="tabpanel-0" 
          aria-labelledby="tab-0" 
          sx={{ 
            pt: 0, // No padding top
            pb: 3, // Keep padding at bottom
            px: 3, // Padding on sides
            position: 'relative', // Needed for absolute positioning of children
            height: 'calc(100vh - 280px)', // Match height with other tabs
            overflowY: 'auto' // Enable scrolling for the tab content
          }}
        >
          {activeTab === 0 && (
            <OneLiner projectId={projectId} />
          )}
        </Box>

        {/* Breakdown Tab Panel */}
        <Box role="tabpanel" hidden={activeTab !== 1} id="tabpanel-1" aria-labelledby="tab-1" sx={{ py: 3, height: 'calc(100vh - 280px)', overflowY: 'auto' }}>
          {activeTab === 1 && (
            <Breakdown projectId={projectId} />
          )}
        </Box>
        
        {/* Shooting Schedule Tab Panel */}
        <Box role="tabpanel" hidden={activeTab !== 2} id="tabpanel-2" aria-labelledby="tab-2" sx={{ py: 3, pl: 2, height: 'calc(100vh - 280px)', overflowY: 'auto' }}>
          {activeTab === 2 && (
            <ShootingScheduleTab projectId={projectId} scenes={scenes} breaks={breaks} sceneBreakdown={sceneBreakdown} />
          )}
        </Box>
        
        {/* Day Out of Days Tab Panel */}
        {dayForDaysEnabled && (
          <Box role="tabpanel" hidden={activeTab !== dayOutTabIndex} id={`tabpanel-${dayOutTabIndex}`} aria-labelledby={`tab-${dayOutTabIndex}`} sx={{ py: 3, height: 'calc(100vh - 280px)', overflowY: 'auto' }}>
            {activeTab === dayOutTabIndex && (
              <DayOutOfDaysTab projectId={projectId} />
            )}
          </Box>
        )}

        {/* Timeline Tab Panel */}
        <Box role="tabpanel" hidden={activeTab !== timelineTabIndex} id={`tabpanel-${timelineTabIndex}`} aria-labelledby={`tab-${timelineTabIndex}`} sx={{ py: 3, pl: 2, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
          {activeTab === timelineTabIndex && (
            <Box sx={{
              width: '100%',
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0
            }}>
              <Typography variant="h5" gutterBottom>Project Timeline</Typography>
              {errorMessages.ganttTasks && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {errorMessages.ganttTasks}
                </Alert>
              )}
              <GanttChart 
                projectTasks={ganttTasks}
                productionPhases={productionPhases}
                initialSelectedPhases={productionPhases.map(p => p.id)}
                onAddTask={async (newTask) => {
                  try {
                    const addedTask = await ganttTaskService.createGanttTask(projectId, newTask);
                    // Server doesn't persist isMilestone; keep client intent for UI
                    setGanttTasks(prev => [...prev, { ...addedTask, isMilestone: newTask.isMilestone }]);
                  } catch (error) {
                    console.error('Error adding Gantt task:', error);
                    setErrorMessages(prev => ({ ...prev, ganttTasks: 'Failed to add Gantt task' }));
                  }
                }}
                onClearAllTasks={async () => {
                  try {
                    // Show loading indicator or disable UI during operation
                    setLoading(true);
                    
                    // Call the API to clear all tasks
                    await ganttTaskService.clearAllGanttTasks(projectId);
                    
                    // Clear the local state
                    setGanttTasks([]);
                    
                    // Show success message
                    setSnackbar({
                      open: true,
                      message: 'All tasks have been cleared successfully',
                      severity: 'success'
                    });
                  } catch (error) {
                    console.error('Error clearing all Gantt tasks:', error);
                    setErrorMessages(prev => ({ ...prev, ganttTasks: 'Failed to clear all tasks' }));
                    
                    // Show error message
                    setSnackbar({
                      open: true,
                      message: 'Failed to clear all tasks',
                      severity: 'error'
                    });
                  } finally {
                    setLoading(false);
                  }
                }}
                onUpdateTask={async (updatedTask) => {
                  try {
                    console.log('Updating task with ID:', updatedTask.id);
                    
                    // Extract the task ID to use for the API call and state updates
                    const taskId = updatedTask.id;
                    
                    // Make a clean version of the task for the API by removing any UI-specific properties
                    const apiTask = { ...updatedTask };
                    // Remove any properties that might have been added by the Gantt chart component
                    delete apiTask.originalId;
                    
                    const updated = await ganttTaskService.updateGanttTask(projectId, taskId, apiTask);
                    
                    // Update the state using the MongoDB _id as the identifier
                    setGanttTasks(prev => {
                      const taskIdStr = taskId != null ? String(taskId) : '';
                      // Find the task to update by matching either id or _id (string compare for robustness)
                      return prev.map(t => {
                        const tid = t?.id != null ? String(t.id) : null;
                        const tMongoId = t?._id != null ? String(t._id) : null;
                        if (tid === taskIdStr || tMongoId === taskIdStr) {
                          console.log('Found task to update:', tid || tMongoId);
                          // Preserve milestone intent for UI (server ignores it)
                          const isMilestone = apiTask?.isMilestone;
                          return { ...updated, isMilestone };
                        }
                        return t;
                      });
                    });
                  } catch (error) {
                    console.error('Error updating Gantt task:', error);
                    setErrorMessages(prev => ({ ...prev, ganttTasks: 'Failed to update Gantt task' }));
                  }
                }}
                onDeleteTask={async (taskId) => {
                  try {
                    console.log('Deleting task with ID:', taskId);
                    await ganttTaskService.deleteGanttTask(projectId, taskId);
                    
                    // Update the state using a more robust filter that checks both id and _id
                    setGanttTasks(prev => prev.filter(t => {
                      // Check against both possible ID fields
                      return t.id !== taskId && t._id !== taskId;
                    }));
                  } catch (error) {
                    console.error('Error deleting Gantt task:', error);
                    setErrorMessages(prev => ({ ...prev, ganttTasks: 'Failed to delete Gantt task' }));
                  }
                }}
              />
            </Box>
        )}
      </Box>
      
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Schedule;
